import React from "react";
import { useIonToast, useIonAlert } from "@ionic/react";
import { InvitationForm } from "../InvitationForm/InvitationForm";
import CollapsibleEntityList from "../CollapsibleEntityList/CollapsibleEntityList";
import InviteeListItem from "../InviteeListItem/InviteeListItem";
import { Invitation, InviteVariables } from "@core/types";
import useGetInviteesQuery from "../../providers/referrals/useGetInviteesQuery";
import useInviteMutation from "../../providers/referrals/useInviteMutation";
import useDeleteInvitationMutation from "../../providers/referrals/useDeleteInvitationMutation";
import { useConfig } from "../../providers/config";
import styles from "./ReferralSystemComponent.module.scss";
import { useTranslation } from "react-i18next";

const ReferralSystemComponent: React.FC = () => {
  const config = useConfig();
  const [presentToast] = useIonToast();
  const [presentAlert] = useIonAlert();
  const {
    data: invitees,
    isRefetching,
    isLoading: isInviteesLoading,
    refetch: refetchInvitees,
  } = useGetInviteesQuery();
  const { mutateAsync: inviteMutation, isLoading: isInviteLoading } = useInviteMutation();
  const deleteInvitationMutation = useDeleteInvitationMutation();
  const { t } = useTranslation();

  const handleInviteSent = async (newInvitee: InviteVariables) => {
    try {
      const result = await inviteMutation(newInvitee);

      if (result) {
        presentToast({
          message: t("Invitation sent successfully"),
          color: "success",
          duration: 2000,
        });
        refetchInvitees();
        return true;
      }
    } catch (error: any) {
      console.log(error);
      const errorMessage = t(error.response?.data?.error as keyof typeof t) || t("Failed to send invitation");
      presentToast({
        message: errorMessage,
        color: "danger",
        duration: 2000,
      });
    }

    return false;
  };

  const handleRemoveInvitee = (inviteeId: string) => {
    presentAlert({
      header: t("Confirm Removal"),
      message: t("Are you sure you want to remove this invitee?"),
      buttons: [
        {
          text: t("Cancel"),
          role: "cancel",
        },
        {
          text: t("Remove"),
          handler: () => {
            deleteInvitationMutation.mutate(
              { id: inviteeId },
              {
                onSuccess: () => {
                  presentToast({
                    message: t("Invitee removed successfully"),
                    color: "success",
                    duration: 2000,
                  });
                  refetchInvitees();
                },
                onError: () => {
                  presentToast({
                    message: t("Failed to remove invitee"),
                    color: "danger",
                    duration: 2000,
                  });
                },
              },
            );
          },
        },
      ],
    });
  };

  const renderInvitee = (invitee: Invitation) => (
    <InviteeListItem key={invitee.id} invitee={invitee} onRemove={handleRemoveInvitee} />
  );

  if (!config?.isInviteEnabled) {
    return null;
  }

  return (
    <>
      <CollapsibleEntityList
        entities={invitees || []}
        isLoading={isInviteesLoading}
        renderItem={renderInvitee}
        renderLoading={() => <p className={styles.inviteMessage}>{t("Loading")}...</p>}
        renderEmpty={() => <p className={styles.inviteMessage}>{t("Invite your contacts to Parhato")}</p>}
      />
      <InvitationForm onInviteSent={handleInviteSent} isLoading={isInviteLoading} />
    </>
  );
};

export default ReferralSystemComponent;
